import React, { useState } from "react";
import { AlertCircle, Clock, CheckCircle2, ChevronDown, History, Eye, MessageSquare } from "lucide-react";
import axios from "axios";
import TaskActivityLog from "./TaskActivityLog";
import TaskDetailModal from "./TaskDetailModal";
import TaskRemarkModal from "./TaskRemarkModal";
import TaskStatusModal from "./TaskStatusModal";
import "../../css/tasks/taskcard.css";

const API_BASE = "https://apicrm.zenrealestate.in/api/tasks";

// Card shows the date only; full date+time is shown in the Details modal.
const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch (e) {
    return "";
  }
};

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "inProgress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

export default function TaskCard({ task, onTaskUpdated }) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showRemark, setShowRemark] = useState(false);
  const [savingRemark, setSavingRemark] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const handleSaveRemark = async (value) => {
    try {
      setSavingRemark(true);
      await axios.patch(
        `${API_BASE}/${task._id}/remark`,
        { remark: value },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      setShowRemark(false);
      onTaskUpdated();
    } catch (error) {
      console.error("Error saving remark:", error);
      console.error("Error response:", error.response?.data);
    } finally {
      setSavingRemark(false);
    }
  };

  // When the status was last changed (dedicated field, or fall back to the log / updatedDate)
  const lastStatusChange = (task.activityLog || [])
    .filter((e) => e.action === "status_changed" || e.action === "completed")
    .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))[0];
  const statusUpdatedAt =
    task.statusUpdatedDate || lastStatusChange?.changedAt || null;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "priority-urgent";
      case "high":
        return "priority-high";
      case "medium":
        return "priority-medium";
      case "low":
        return "priority-low";
      default:
        return "priority-medium";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "status-pending";
      case "inProgress":
        return "status-in-progress";
      case "completed":
        return "status-completed";
      case "overdue":
        return "status-on-hold";
      default:
        return "status-pending";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "inProgress":
        return "In Progress";
      case "completed":
        return "Completed";
      case "overdue":
        return "Overdue";
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock size={16} />;
      case "inProgress":
        return <AlertCircle size={16} />;
      case "completed":
        return <CheckCircle2 size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const handleStatusChange = async (newStatus, remark = "") => {
    if (!newStatus || newStatus === task.status) return;
    try {
      setIsUpdating(true);
      await axios.patch(
        `${API_BASE}/${task._id}/status`,
        { status: newStatus, remark },
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        }
      );
      setShowStatusModal(false);
      setPendingStatus(null);
      onTaskUpdated();
    } catch (error) {
      console.error("Error updating task status:", error);
      console.error("Error response:", error.response?.data);
    } finally {
      setIsUpdating(false);
    }
  };

  const daysUntilDue = task.dueDate
    ? Math.ceil((new Date(task.dueDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;

  return (
    <div className="tc-card">
      <div className="tc-header">
        <h3
          className="tc-title tc-title-link"
          onClick={() => setShowDetail(true)}
          title="View full details"
        >
          {task.title || task.taskTitle}
        </h3>
        <span className={"tc-priority " + getPriorityColor(task.priority)}>
          {task.priority}
        </span>
      </div>

      {task.description && (
        <p
          className="tc-description tc-description-link"
          onClick={() => setShowDetail(true)}
          title="View full details"
        >
          {task.description}
        </p>
      )}

      <div className="tc-meta">
        <div className="tc-meta-item">
          <span className="tc-label">Status</span>
          <div className={"tc-status-badge " + getStatusColor(task.status)}>
            {getStatusIcon(task.status)}
            {getStatusLabel(task.status)}
          </div>
          {statusUpdatedAt && (
            <span className="tc-status-updated">Updated {fmtDate(statusUpdatedAt)}</span>
          )}
        </div>

        {task.dueDate && (
          <div className="tc-meta-item">
            <span className="tc-label">Target</span>
            <span className={isOverdue ? "tc-overdue" : "tc-due-date"}>
              {new Date(task.dueDate).toLocaleDateString()}
            </span>
          </div>
        )}

        {task.remark && (
          <div className="tc-meta-item">
            <span className="tc-remark-label">Remark</span>
            <div className="tc-remark">{task.remark}</div>
          </div>
        )}
      </div>

      <div className="tc-footer">
        <div className="tc-status-control">
          <label className="tc-label">Update Status</label>
          <div className="tc-select-wrap">
            <select
              className="tc-status-select"
              value={task.status === "overdue" ? "pending" : task.status}
              onChange={(e) => {
                const v = e.target.value;
                if (v && v !== task.status) {
                  setPendingStatus(v);
                  setShowStatusModal(true);
                }
              }}
              disabled={isUpdating}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="tc-select-arrow" />
          </div>
        </div>

        <div className="tc-footer-btns">
          <button className="tc-activity-btn" onClick={() => setShowRemark(true)}>
            <MessageSquare size={14} /> Remark
          </button>
          <button className="tc-activity-btn" onClick={() => setShowDetail(true)}>
            <Eye size={14} /> Details
          </button>
          <button className="tc-activity-btn" onClick={() => setShowLog(true)}>
            <History size={14} /> Activity
          </button>
        </div>
      </div>

      {showLog && (
        <TaskActivityLog log={task.activityLog || []} onClose={() => setShowLog(false)} />
      )}

      {showDetail && (
        <TaskDetailModal task={task} onClose={() => setShowDetail(false)} />
      )}

      {showRemark && (
        <TaskRemarkModal
          initialValue={task.remark || ""}
          saving={savingRemark}
          onSave={handleSaveRemark}
          onClose={() => setShowRemark(false)}
        />
      )}

      {showStatusModal && pendingStatus && (
        <TaskStatusModal
          currentStatus={task.status}
          newStatus={pendingStatus}
          saving={isUpdating}
          onSave={(remark) => handleStatusChange(pendingStatus, remark)}
          onClose={() => { setShowStatusModal(false); setPendingStatus(null); }}
        />
      )}
    </div>
  );
}
